// Opinioes.js — Seção "Opiniões" (avaliações) da página de produto:
// média + distribuição de notas à esquerda, lista de avaliações à direita e
// formulário para escrever uma avaliação.
//
// MUDANÇA DE 19/08/2026 — SÓ QUEM COMPROU AVALIA:
// antes, qualquer usuário logado avaliava qualquer livro do acervo. Agora o
// backend recusa com 403 quem não comprou, e esta tela pergunta ANTES
// (`usePodeAvaliar`) para não oferecer um formulário que o servidor vai
// rejeitar depois de a pessoa escrever o comentário inteiro.
//
// Quatro estados, cada um com uma saída:
//   não logado ................ convite para entrar
//   logado, não comprou ....... explica a regra e leva ao cartão de preço
//   logado, comprou ........... formulário + "ganhe N Insigne Coins"
//   já avaliou ................ a própria avaliação + botão de editar
//
// O selo "Compra verificada" SAIU da lista: agora vale para todas as avaliações
// novas, e um selo que todo mundo tem não distingue ninguém — só ocupa espaço.
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Stars from "./Stars";
import useAvaliacoes from "../../../hooks/useAvaliacoes";
import usePodeAvaliar from "../../../hooks/usePodeAvaliar";
import { useAuth } from "../../../services/context/AuthContext";
import Reveal from "../../ui/Reveal";
import FotoPessoa from "../../ui/FotoPessoa";
import Plaquinhas from "../../ui/Plaquinhas";
import CabecalhoDeSecao from "./CabecalhoDeSecao";
import AvaliacaoEditorial from "./AvaliacaoEditorial";

const main = process.env.REACT_APP_MAIN;
const API_URL = process.env.REACT_APP_API;

const fmtData = (iso) => {
  if (!iso) return "";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR");
};

// Ordenações da lista. Existe porque uma lista só por data esconde a
// informação mais útil (a crítica) atrás das mais recentes.
const ORDENS = [
  { key: "recentes", label: "Mais recentes" },
  { key: "maior", label: "Maior nota" },
  { key: "menor", label: "Menor nota" },
];

// Seletor de nota (estrelas clicáveis) para o formulário
const StarInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const ativo = hover || value;
  return (
    <div className="inline-flex items-center gap-1 text-2xl text-insigne-orange" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
          className="hover:scale-110 transition-transform"
        >
          <i className={"bi " + (ativo >= n ? "bi-star-fill" : "bi-star")} />
        </button>
      ))}
    </div>
  );
};

// Caixa de aviso dos estados em que não há formulário. Mesmo desenho do
// formulário (card escuro), para a seção não terminar num vazio.
const Aviso = ({ icone, titulo, children }) => (
  <div className="bloco p-[1.125rem] sm:p-6">
    <div className="flex items-start gap-3">
      <i className={"bi " + icone + " text-xl text-insigne-orange mt-0.5"} />
      <div className="min-w-0">
        <p className="font-semibold text-white">{titulo}</p>
        <div className="text-sm text-white/60 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  </div>
);

const Opinioes = ({ ebookId }) => {
  const { user } = useAuth();
  const {
    avaliacoes, editoriais, media, distribuicao, total, loading, enviando, enviar,
    podeResponder, respondendo, responder, removerResposta,
  } = useAvaliacoes(ebookId);

  // Qual comentário tem a caixa de resposta aberta, e o que está escrito nela.
  // Uma só por vez: duas caixas abertas fazem a pessoa perder de vista a quem
  // está respondendo.
  const [respondendoId, setRespondendoId] = useState(null);
  const [textoResposta, setTextoResposta] = useState("");
  const [erroResposta, setErroResposta] = useState("");
  const permissao = usePodeAvaliar(ebookId);

  const [nota, setNota] = useState(0);
  const [comentario, setComentario] = useState("");
  const [feedback, setFeedback] = useState(null); // { tipo: "erro" | "ok", texto }
  const [editando, setEditando] = useState(false);
  const [ordem, setOrdem] = useState("recentes");
  const [denunciadas, setDenunciadas] = useState([]);

  // ===== O LINK DO E-MAIL DE CONVITE (07/10/2026) =====
  //
  // `?avaliar=1&nota=4&de=convite` chega do e-mail de ConvidarAvaliacoes: cada
  // estrela do e-mail é um link com a nota. Medido antes desta mudança: 35
  // convites, zero avaliações. O link levava à página do livro, e dali a
  // pessoa ainda tinha de achar o formulário, descer até ele e, sem login,
  // entrar e ser largada no perfil.
  //
  // Agora a página desce sozinha até aqui, marca a nota que a pessoa tocou no
  // e-mail e põe o cursor no comentário. Uma vez só por visita (`tratado`):
  // depois disso a página é dela, e puxar a rolagem de novo a cada nova
  // consulta da permissão seria brigar com o dedo de quem lê.
  const location = useLocation();
  const pedido = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const n = parseInt(q.get("nota"), 10);
    const de = q.get("de");
    return {
      avaliar: q.get("avaliar") === "1",
      nota: n >= 1 && n <= 5 ? n : 0,
      origem: de === "convite" || de === "lembrete" ? de : null,
    };
  }, [location.search]);
  const tratado = useRef(false);
  const campoRef = useRef(null);

  useEffect(() => {
    if (!pedido.avaliar || tratado.current || permissao.loading) return undefined;
    tratado.current = true;
    if (pedido.nota && permissao.pode && !permissao.jaAvaliou) setNota(pedido.nota);
    // Um respiro para a página terminar de montar (capa, preço) antes de
    // medir onde a seção está: rolar cedo demais para num ponto que depois
    // é empurrado para baixo pelo que carregou em cima.
    const t = setTimeout(() => {
      const secao = document.getElementById("opinioes");
      if (secao) secao.scrollIntoView({ behavior: "smooth", block: "start" });
      if (campoRef.current) setTimeout(() => campoRef.current && campoRef.current.focus({ preventScroll: true }), 600);
    }, 400);
    return () => clearTimeout(t);
  }, [pedido, permissao.loading, permissao.pode, permissao.jaAvaliou]);

  // Quando a pessoa clica em "Editar", o formulário nasce preenchido com o que
  // ela escreveu — reescrever tudo do zero seria motivo suficiente para
  // desistir da correção.
  const abrirEdicao = () => {
    setNota(permissao.avaliacao?.nota || 0);
    setComentario(permissao.avaliacao?.comentario || "");
    setFeedback(null);
    setEditando(true);
  };

  // Livro sem leitor nenhum, só com a Avaliação Editorial: o resumo conta as
  // notas DA EDITORA, e diz que são da editora. Não se misturam com as dos
  // leitores (aqui, no topo da página, nos cards e no Google): nota da
  // própria editora somada à de quem comprou seria nota inflada.
  const resumoEditora = useMemo(() => {
    if (total > 0 || !editoriais.length) return null;
    const soma = editoriais.reduce((s, c) => s + (Number(c.nota) || 5), 0);
    return { media: soma / editoriais.length, n: editoriais.length };
  }, [total, editoriais]);

  const listaOrdenada = useMemo(() => {
    const copia = [...avaliacoes];
    if (ordem === "maior") copia.sort((a, b) => b.nota - a.nota);
    else if (ordem === "menor") copia.sort((a, b) => a.nota - b.nota);
    // O Selo Insigne fixa o comentário no topo — inclusive quando a pessoa
    // reordena. Um destaque que some ao clicar em "menor nota" não é destaque;
    // é uma ordenação que o painel não controla.
    copia.sort((a, b) => (b.selo_insigne ? 1 : 0) - (a.selo_insigne ? 1 : 0));
    return copia;
  }, [avaliacoes, ordem]);

  const submeter = async () => {
    setFeedback(null);
    if (!nota) { setFeedback({ tipo: "erro", texto: "Selecione uma nota." }); return; }
    if (comentario.trim().length < 3) { setFeedback({ tipo: "erro", texto: "Escreva um comentário." }); return; }
    try {
      const resultado = await enviar({ nota, comentario: comentario.trim(), origem: pedido.origem });
      const coins = Number(resultado?.coins_creditados) || 0;
      setNota(0);
      setComentario("");
      setEditando(false);
      setFeedback({
        tipo: "ok",
        texto: coins
          ? `Avaliação publicada. Você ganhou ${coins} Insigne Coins!`
          : "Avaliação atualizada. Obrigado!",
      });
      // Reconsulta a permissão: o estado da tela passa de "pode avaliar" para
      // "já avaliou", com o botão de editar no lugar do formulário.
      permissao.recarregar();
    } catch (e) {
      setFeedback({ tipo: "erro", texto: e?.message || "Não foi possível enviar sua avaliação." });
    }
  };

  const denunciar = async (id) => {
    if (denunciadas.includes(id)) return;
    setDenunciadas((l) => [...l, id]);
    try {
      await fetch(`${API_URL}avaliacoes/${id}/denunciar`, {
        method: "POST",
        headers: { Accept: "application/json" },
      });
    } catch { /* a denúncia não é crítica: falhar em silêncio é aceitável */ }
  };

  // ===== Qual bloco vai embaixo da lista =====
  const renderAcao = () => {
    if (permissao.loading) {
      return <div className="h-24 rounded-2xl bg-insigne-navy-soft animate-pulse" aria-hidden="true" />;
    }

    if (!user || permissao.motivo === "nao_logado") {
      return (
        <Aviso icone="bi-box-arrow-in-right" titulo="Entre para avaliar">
          As avaliações são de quem comprou o livro.{" "}
          {/* O destino vai junto (ver destinoAposEntrar.js): quem chegou
              pelo e-mail de convite entra e volta para ESTE formulário, com a
              nota que tocou — e não para o perfil, de onde não acharia o
              caminho de volta. */}
          <Link
            to={main + "login"}
            state={{ de: location.pathname + location.search }}
            className="text-insigne-orange hover:underline"
          >
            Entre na sua conta
          </Link>{" "}
          para deixar a sua.
        </Aviso>
      );
    }

    // Assinante do Clube que ainda não leu o bastante. Antes caía no aviso
    // abaixo, que manda COMPRAR — o conselho errado para quem já pode ler o
    // livro inteiro, e o único que a vitrine do Clube não pode dar. A regra é
    // do servidor (`avaliacao_percentual_minimo_assinante`); aqui só se conta.
    if (!permissao.pode && permissao.motivo === "leitura_insuficiente") {
      return (
        <Aviso icone="bi-hourglass-split" titulo="Avaliar libera no meio da leitura">
          Quem lê pelo Clube avalia depois de ler{" "}
          {permissao.percentualMinimo > 0 ? `${permissao.percentualMinimo}%` : "metade"} do livro. É o
          que mantém as notas de quem leu de verdade.
          {permissao.percentualLido > 0 && <> Você está em {permissao.percentualLido}%.</>}
        </Aviso>
      );
    }

    if (!permissao.pode) {
      return (
        <Aviso icone="bi-bag-check" titulo="Só quem comprou pode avaliar">
          É o que mantém as notas confiáveis: toda opinião aqui é de um leitor que
          pagou pelo livro.{" "}
          <button
            type="button"
            onClick={() => {
              const alvo = document.getElementById("cartao-de-preco");
              if (alvo) alvo.scrollIntoView({ behavior: "smooth", block: "center" });
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="text-insigne-orange hover:underline"
          >
            Ver as opções de compra
          </button>
          .
        </Aviso>
      );
    }

    // Já avaliou e não está editando → mostra a própria avaliação.
    if (permissao.jaAvaliou && !editando) {
      return (
        <div className="bloco p-[1.125rem] sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/45 mb-3">
            Sua avaliação
          </p>
          <Stars value={permissao.avaliacao?.nota || 0} size="text-lg" />
          <p className="text-white/80 text-sm mt-2 leading-relaxed whitespace-pre-line">
            {permissao.avaliacao?.comentario}
          </p>
          {feedback && (
            <p className={"text-xs mt-3 " + (feedback.tipo === "ok" ? "text-emerald-400" : "text-amber-300")}>
              {feedback.texto}
            </p>
          )}
          <button
            onClick={abrirEdicao}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border-2 border-insigne-orange
                       px-5 py-2 text-sm font-semibold text-insigne-orange
                       transition-colors hover:bg-insigne-orange hover:text-paper"
          >
            <i className="bi bi-pencil" /> Editar avaliação
          </button>
          <p className="text-[11px] text-white/40 mt-2">
            Editar não gera novos Insigne Coins: o bônus é pago uma vez por livro.
          </p>
        </div>
      );
    }

    // Formulário: primeira avaliação ou edição.
    return (
      <div className="bloco p-[1.125rem] sm:p-6">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="text-sm text-white/70">Sua nota:</span>
          <StarInput value={nota} onChange={setNota} />
        </div>
        <textarea
          ref={campoRef}
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="O que você achou do livro?"
          className="w-full rounded-lg bg-insigne-navy-deep border border-line px-3.5 py-2.5 text-sm
                     text-white placeholder:text-white/40 outline-none focus:border-insigne-orange
                     transition-colors resize-y"
        />
        {feedback && (
          <p className={"text-xs mt-2 " + (feedback.tipo === "ok" ? "text-emerald-400" : "text-amber-300")}>
            {feedback.texto}
          </p>
        )}
        <div className="flex gap-2.5 mt-3">
          <button
            onClick={submeter}
            disabled={enviando}
            className="btn-solido flex-1 rounded-controle py-2.5 font-semibold text-sm disabled:opacity-60"
          >
            {enviando ? "Enviando..." : editando ? "Salvar alteração" : "Publicar avaliação"}
          </button>
          {editando && (
            <button
              onClick={() => { setEditando(false); setFeedback(null); }}
              className="rounded-controle border-2 border-line px-5 py-2.5 text-sm font-semibold text-white/70
                         hover:text-white hover:border-white/30 transition-colors"
            >
              Cancelar
            </button>
          )}
        </div>
        {!editando && (
          // O bônus é o motivo pelo qual a pessoa volta para avaliar. Dizer o
          // número aqui, e não numa página de ajuda, é o que faz diferença.
          <p className="flex items-center gap-1.5 text-[11px] text-insigne-orange mt-2 font-semibold">
            <i className="bi bi-coin" />
            Ganhe {permissao.coins} Insigne Coins ao publicar sua primeira avaliação deste livro.
          </p>
        )}
      </div>
    );
  };

  return (
    // `scroll-mt-32`: as estrelas do topo rolam até aqui, e sem a folga o
    // cabeçalho fixo do site cobria o título da seção na chegada.
    <Reveal as="section" id="opinioes" className="mb-12 scroll-mt-32" aria-labelledby="opinioes-titulo">
      {/* Mesmo cabeçalho de todas as seções da página — ver CabecalhoDeSecao. */}
      <CabecalhoDeSecao chapeu="Quem leu" titulo="Opiniões" id="opinioes-titulo" />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Resumo de notas */}
        <div>
          {resumoEditora ? (
            <>
              <div className="flex items-center gap-3">
                <span className="font-display text-[3.4rem] leading-none">{resumoEditora.media.toFixed(1).replace(".", ",")}</span>
                <Stars value={resumoEditora.media} size="text-xl" />
              </div>
              <p className="text-white/50 text-sm mt-1">
                {resumoEditora.n} {resumoEditora.n === 1 ? "avaliação" : "avaliações"} da editora
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="font-display text-[3.4rem] leading-none">{total ? media.toFixed(1).replace(".", ",") : "-"}</span>
                <Stars value={media} size="text-xl" />
              </div>
              <p className="text-white/50 text-sm mt-1">
                {total} {total === 1 ? "avaliação" : "avaliações"}
              </p>
            </>
          )}
          {/* AS BARRAS SÓ EXISTEM COM ALGUMA AVALIAÇÃO (17/09/2026). Num livro
              sem nenhuma, eram cinco barras vazias terminando em "0" — ~130px
              no celular dizendo cinco vezes a mesma coisa que o "0 avaliações"
              logo acima já disse. */}
          {total > 0 && (
          <div className="mt-4 space-y-1.5">
            {[5, 4, 3, 2, 1].map((n) => {
              const qtd = distribuicao[n] || 0;
              const pct = total ? (qtd / total) * 100 : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-sm">
                  <span className="w-3 text-white/60">{n}</span>
                  <div className="flex-1 h-1.5 rounded-selo bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-insigne-orange transition-[width] duration-700 ease-out"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-6 text-right text-white/40 text-xs">{qtd}</span>
                </div>
              );
            })}
          </div>
          )}
        </div>

        {/* Lista + ação */}
        <div>
          {/* Ordenação só aparece com lista suficiente para ordenar mudar algo. */}
          {avaliacoes.length > 2 && (
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs text-white/45">Ordenar por:</span>
              {ORDENS.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setOrdem(o.key)}
                  className={
                    "rounded-controle px-3 py-1 text-xs font-semibold transition-colors " +
                    (ordem === o.key
                      ? "bg-insigne-orange text-paper"
                      : "border border-line text-white/60 hover:text-white")
                  }
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="space-y-5 mb-6" aria-hidden="true">
              {[0, 1].map((i) => (
                <div key={i} className="animate-pulse border-b border-line pb-[1.25rem]">
                  <div className="h-8 w-40 rounded-selo bg-white/10" />
                  <div className="h-3 w-24 rounded bg-white/10 mt-3" />
                  <div className="h-3 w-full rounded bg-white/10 mt-3" />
                  <div className="h-3 w-4/5 rounded bg-white/10 mt-2" />
                </div>
              ))}
            </div>
          ) : avaliacoes.length === 0 ? (
            // Sem leitor nenhum, a editora apresenta o livro (quando o painel
            // deixa). Assinado e com selo: ver AvaliacaoEditorial.
            editoriais.length > 0 ? (
              <AvaliacaoEditorial itens={editoriais} />
            ) : (
              <p className="text-white/50 text-sm mb-6">
                Ainda não há avaliações para este livro.
              </p>
            )
          ) : (
            <div className="space-y-5 mb-6">
              {listaOrdenada.map((a, i) => (
                <div
                  key={a.id || i}
                  className={
                    "group/aval border-b border-line pb-[1.25rem] last:border-0 " +
                    // O comentário selado ganha moldura própria: fixá-lo no topo
                    // sem marcá-lo deixaria a pessoa achando que a lista está
                    // fora de ordem.
                    (a.selo_insigne
                      ? "relative rounded-2xl border border-insigne-orange/40 bg-insigne-orange/[0.06] p-4 md:p-5 mb-1"
                      : "")
                  }
                >
                  {a.selo_insigne && (
                    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider
                                  text-insigne-orange mb-2.5">
                      <i className="bi bi-award-fill" /> Destaque da editora
                    </p>
                  )}
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <FotoPessoa
                      pessoa={a}
                      nome={a.usuario_nome || a.nome}
                      tamanho="w-9 h-9"
                      className="text-base"
                    />
                    <span className="font-bold text-sm">{a.usuario_nome || a.nome || "Usuário"}</span>
                    <Plaquinhas
                      autor={a.autor}
                      assinante={a.assinante}
                      aprovadoAutor={a.aprovado_autor}
                      selo={a.selo_insigne}
                    />
                    {/* Denunciar: só levanta a mão para a moderação do painel.
                        Discreto de propósito — é uma saída, não uma ação
                        que a seção esteja convidando ninguém a usar. */}
                    <button
                      onClick={() => denunciar(a.id)}
                      disabled={denunciadas.includes(a.id)}
                      title={denunciadas.includes(a.id) ? "Denúncia registrada" : "Denunciar esta avaliação"}
                      className="ml-auto text-[11px] text-white/25 hover:text-amber-300 transition-colors
                                 disabled:text-emerald-400/70 disabled:cursor-default"
                    >
                      <i className={"bi " + (denunciadas.includes(a.id) ? "bi-check2" : "bi-flag")} />{" "}
                      {denunciadas.includes(a.id) ? "Denunciada" : "Denunciar"}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-white/50">
                    <Stars value={a.nota} size="text-xs" />
                    <span>{fmtData(a.created_at)}</span>
                  </div>
                  <p className="text-white/80 text-sm mt-2 leading-relaxed whitespace-pre-line">
                    {a.comentario || a.texto}
                  </p>

                  {/* ============ RESPOSTAS DO AUTOR ============
                      Recuadas e com uma linha à esquerda: quem lê precisa ver de
                      relance que aquilo é réplica, e não outro comentário. Só o
                      autor DO LIVRO responde — nem admin —, e a plaquinha "Autor"
                      ao lado do nome diz por que aquela voz tem peso ali. */}
                  {(a.respostas || []).length > 0 && (
                    <div className="mt-3 space-y-3 border-l-2 border-insigne-orange/30 pl-4">
                      {a.respostas.map((r) => (
                        <div key={r.id}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <FotoPessoa pessoa={r} nome={r.usuario_nome} tamanho="w-7 h-7" className="text-sm" />
                            <span className="font-bold text-sm">{r.usuario_nome || "Autor"}</span>
                            <Plaquinhas autor={r.autor} />
                            <span className="text-xs text-white/40">{fmtData(r.created_at)}</span>
                            {/* `minha`, e nao `podeResponder`: um livro aceita
                                varios autores, e o botao so pode aparecer para
                                quem escreveu ESTA resposta. */}
                            {r.minha && (
                              <button
                                onClick={() => removerResposta(r.id)}
                                className="ml-auto text-[11px] text-white/25 hover:text-red-400 transition-colors"
                                title="Apagar esta resposta"
                              >
                                <i className="bi bi-trash" /> Apagar
                              </button>
                            )}
                          </div>
                          <p className="text-white/75 text-sm mt-1.5 leading-relaxed whitespace-pre-line">
                            {r.texto}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {podeResponder && (
                    <div className="mt-3">
                      {respondendoId === a.id ? (
                        <div className="border-l-2 border-insigne-orange/30 pl-4">
                          <textarea
                            value={textoResposta}
                            onChange={(e) => setTextoResposta(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            autoFocus
                            placeholder="Responda como autor do livro…"
                            className="w-full rounded-lg border border-line bg-insigne-navy-deep px-3 py-2
                                       text-sm text-white placeholder:text-white/30 focus:border-insigne-orange
                                       focus:outline-none"
                          />
                          {erroResposta && (
                            <p className="text-red-400 text-xs mt-1.5">{erroResposta}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              disabled={respondendo || !textoResposta.trim()}
                              onClick={async () => {
                                setErroResposta("");
                                try {
                                  await responder(a.id, textoResposta.trim());
                                  setRespondendoId(null);
                                  setTextoResposta("");
                                } catch (e) {
                                  setErroResposta(e.message || "Não foi possível publicar.");
                                }
                              }}
                              className="btn-solido text-sm disabled:opacity-50"
                            >
                              {respondendo ? "Publicando…" : "Publicar resposta"}
                            </button>
                            <button
                              onClick={() => { setRespondendoId(null); setTextoResposta(""); setErroResposta(""); }}
                              className="text-sm text-white/50 hover:text-white transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setRespondendoId(a.id); setTextoResposta(""); setErroResposta(""); }}
                          className="text-xs font-semibold text-insigne-orange hover:underline"
                        >
                          <i className="bi bi-reply-fill" /> Responder
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {renderAcao()}
        </div>
      </div>
    </Reveal>
  );
};

export default Opinioes;
